import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import Close from "@material-ui/icons/Close";
import Search from "@material-ui/icons/Search";
import Folder from "@material-ui/icons/Folder";
import InsertDriveFileOutlined from "@material-ui/icons/InsertDriveFileOutlined";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ArrowBack from "@material-ui/icons/ArrowBack";
import MyDocsApi, { MY_DOCS_ROOT, parentFolder } from "../../../../assets/js/utils/myDocs/myDocsApi";
import farDialogStyle from "../../../../assets/jss/user/far/farDialogStyle.jsx";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";
import { helper } from "../../../../assets/js/utils/Element";
import { FarNotice } from "../Far/farUi";
import LoaderComponent from "../../Loader";

const useStyles = makeStyles(theme => {
    const base = farDialogStyle(theme);
    const s = farShared(theme);
    const t = s.t;
    return Object.assign({}, base, {
        paper: Object.assign({}, base.paper, { maxWidth: "720px" }),
        body: Object.assign({}, base.body, { gap: "12px", paddingTop: "14px", paddingBottom: "14px" }),
        toolbar: { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" },
        searchWrap: {
            position: "relative", flex: "1 1 220px",
            "& svg": { position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", width: "18px", height: "18px", color: t.textMuted }
        },
        search: Object.assign({}, s.input, { paddingLeft: "34px" }),
        crumbs: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "2px", margin: 0, padding: 0, listStyle: "none", fontSize: "14px" },
        crumb: {
            ...s.focusRing, border: "none", background: "transparent", color: t.dark ? t.link : t.accent, cursor: "pointer",
            font: "inherit", fontWeight: 600, padding: "2px 4px", borderRadius: "4px",
            "&:hover": { textDecoration: "underline" },
            "&[aria-current='page']": { color: t.textStrong, cursor: "default", textDecoration: "none" }
        },
        crumbSep: { width: "16px", height: "16px", color: t.textMuted },
        list: {
            margin: 0, padding: 0, listStyle: "none", border: "1px solid "+t.border, borderRadius: "10px",
            maxHeight: "min(52vh, 420px)", minHeight: "180px", overflowY: "auto"
        },
        row: {
            display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", minHeight: "48px",
            borderTop: "1px solid "+t.border, "&:first-child": { borderTop: "none" },
            "&:hover": { backgroundColor: t.surfaceHover }
        },
        rowSelected: { backgroundColor: (t.dark ? "rgba(59,130,246,0.10)" : "#F1F6FF")+" !important" },
        rowCheck: { width: "18px", height: "18px", accentColor: t.accent, cursor: "pointer", margin: 0, flexShrink: 0 },
        rowIcon: { width: "22px", height: "22px", flexShrink: 0 },
        folderIcon: { color: t.dark ? "#FAAF42" : "#D97706" },
        fileIcon: { color: t.textMuted },
        rowText: { flex: "1 1 auto", minWidth: 0 },
        rowName: { margin: 0, fontSize: "14px", fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        rowMeta: { margin: "2px 0 0", fontSize: "12px", color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
        openButton: {
            ...s.focusRing, display: "inline-flex", alignItems: "center", gap: "2px", border: "none", background: "transparent",
            color: t.dark ? t.link : t.accent, cursor: "pointer", font: "inherit", fontSize: "13px", fontWeight: 600, padding: "6px 8px", borderRadius: "6px",
            flexShrink: 0, "&:hover": { backgroundColor: t.info.bg }, "& svg": { width: "18px", height: "18px" }
        },
        nameButton: {
            ...s.focusRing, display: "block", width: "100%", textAlign: "left", border: "none", background: "transparent", padding: 0,
            cursor: "pointer", font: "inherit", color: "inherit"
        },
        empty: { padding: "40px 16px", textAlign: "center", color: t.textMuted, fontSize: "14px" },
        footerInfo: { marginRight: "auto", alignSelf: "center", fontSize: "14px", color: t.textMuted },
        current: { margin: 0, fontSize: "14px", color: t.text, wordBreak: "break-word", "& strong": { color: t.textStrong } }
    });
});

const crumbsOf = (path) => {
    const parts = String(path || MY_DOCS_ROOT).split("/").filter(Boolean);
    return parts.map((name, i) => ({ name, path: "/"+parts.slice(0, i + 1).join("/") }));
};

/**
 * Browses the signed-in user's My Docs.
 *   mode "items":  pick files and folders (Send page). onSelect(rows)
 *   mode "folder": pick one folder (FAR rule Sendlinx folder). onSelect(path)
 */
function MyDocsPickerModal({ open, mode, title, subtitle, initialPath, selected, onClose, onSelect }){
    const classes = useStyles();
    const isFolderMode = mode === "folder";
    const [path, setPath] = React.useState(initialPath && initialPath.indexOf(MY_DOCS_ROOT) === 0 ? initialPath : MY_DOCS_ROOT);
    const [query, setQuery] = React.useState("");
    const [search, setSearch] = React.useState("");
    const [items, setItems] = React.useState([]);
    const [truncated, setTruncated] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");
    const [picked, setPicked] = React.useState(() => {
        const map = {};
        (selected || []).forEach(row => { map[row.id] = row; });
        return map;
    });
    const sourceRef = React.useRef(null);
    const listRef = React.useRef(null);
    const titleRef = React.useRef(null);

    const load = React.useCallback((nextPath, nextSearch) => {
        if(sourceRef.current){ sourceRef.current.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        sourceRef.current = source;
        setLoading(true);
        setError("");
        MyDocsApi.browse(nextPath, nextSearch, source).then(body => {
            let rows = (body && body.items) || [];
            if(isFolderMode){
                rows = rows.filter(r => r.type === "folder");
            }
            setItems(rows);
            setTruncated(!!(body && body.truncated));
            setLoading(false);
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            setItems([]);
            setLoading(false);
            setError((err && err.message) || "My Docs could not be loaded.");
        });
    }, [isFolderMode]);

    React.useEffect(() => { load(path, search); }, [path, search, load]);
    React.useEffect(() => () => { if(sourceRef.current){ sourceRef.current.cancel("Request Cancelled"); } }, []);
    React.useEffect(() => {
        if(open && titleRef.current){ titleRef.current.focus(); }
    }, [open]);

    const openFolder = (folderPath) => {
        setQuery("");
        setSearch("");
        setPath(folderPath);
        if(listRef.current){ listRef.current.scrollTop = 0; }
    };
    const submitSearch = (e) => {
        e.preventDefault();
        setSearch(query.trim());
    };
    const toggle = (row) => {
        setPicked(prev => {
            const next = Object.assign({}, prev);
            if(next[row.id]){ delete next[row.id]; }else{ next[row.id] = row; }
            return next;
        });
    };
    const pickedRows = Object.keys(picked).map(id => picked[id]);
    const pickedSize = pickedRows.reduce((sum, r) => sum + (r.size || 0), 0);
    const visibleSelectable = items;
    const allVisiblePicked = visibleSelectable.length > 0 && visibleSelectable.every(r => picked[r.id]);
    const toggleAllVisible = () => {
        setPicked(prev => {
            const next = Object.assign({}, prev);
            visibleSelectable.forEach(r => { if(allVisiblePicked){ delete next[r.id]; }else{ next[r.id] = r; } });
            return next;
        });
    };
    const crumbs = crumbsOf(path);
    const canGoUp = path !== MY_DOCS_ROOT;
    const headingId = "mydocs-picker-title";

    const confirm = () => {
        if(isFolderMode){
            onSelect(path);
            return;
        }
        // A file inside a picked folder is already included: keep only the outermost picks.
        const folders = pickedRows.filter(r => r.type === "folder").map(r => r.path);
        const rows = pickedRows.filter(r => !folders.some(f => r.path !== f && r.path.indexOf(f+"/") === 0));
        onSelect(rows);
    };

    return (
        <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} aria-labelledby={headingId} scroll="body">
            <div className={classes.header}>
                <div>
                    <h2 className={classes.title} id={headingId} tabIndex={-1} ref={titleRef}>{title}</h2>
                    { subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null }
                </div>
                <button type="button" className={classes.close} aria-label="Close" onClick={onClose}><Close /></button>
            </div>
            <div className={classes.body}>
                <nav aria-label="Folder path">
                    <ol className={classes.crumbs}>
                        {
                            crumbs.map((crumb, i) => (
                                <li key={crumb.path} style={{display: "inline-flex", alignItems: "center"}}>
                                    { i > 0 ? <ChevronRight className={classes.crumbSep} aria-hidden="true" /> : null }
                                    <button type="button" className={classes.crumb} aria-current={crumb.path === path && !search ? "page" : undefined}
                                        onClick={() => (crumb.path === path && !search ? null : openFolder(crumb.path))}>{crumb.name}</button>
                                </li>
                            ))
                        }
                    </ol>
                </nav>
                <div className={classes.toolbar}>
                    { canGoUp ? <button type="button" className={classes.cancel} onClick={() => openFolder(parentFolder(path))}><ArrowBack style={{width: 18, height: 18}} aria-hidden="true" />Up</button> : null }
                    {
                        !isFolderMode ?
                            <form className={classes.searchWrap} onSubmit={submitSearch} role="search">
                                <Search aria-hidden="true" />
                                <label htmlFor="mydocs-picker-search" style={{position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)"}}>Search in this folder</label>
                                <input id="mydocs-picker-search" type="search" className={classes.search} placeholder="Search in this folder and subfolders… (Enter)"
                                    value={query} onChange={(e) => { setQuery(e.target.value); if(e.target.value === "" && search){ setSearch(""); } }} />
                            </form>
                        : null
                    }
                    {
                        !isFolderMode && visibleSelectable.length > 0 ?
                            <label className={classes.check}>
                                <input type="checkbox" checked={allVisiblePicked} onChange={toggleAllVisible} />
                                Select all
                            </label>
                        : null
                    }
                </div>
                { search ? <p className={classes.current} role="status">Results for <strong>“{search}”</strong> in {path}</p> : null }
                { error ? <FarNotice tone="danger" action={<button type="button" className={classes.cancel} onClick={() => load(path, search)}>Retry</button>}>{error}</FarNotice> : null }
                <ul className={classes.list} ref={listRef} aria-label={isFolderMode ? "Folders" : "Files and folders"} aria-busy={loading ? "true" : "false"}>
                    {
                        loading ?
                            <li className={classes.empty}><LoaderComponent color="custom" align="center" /></li>
                        : items.length <= 0 && !error ?
                            <li className={classes.empty}>{ search ? "No files or folders match your search." : (isFolderMode ? "This folder has no subfolders." : "This folder is empty.") }</li>
                        :
                            items.map(row => {
                                const isFolder = row.type === "folder";
                                const isPicked = !!picked[row.id];
                                const checkId = "mydocs-pick-"+row.id;
                                const meta = [
                                    search ? parentFolder(row.path) : null,
                                    isFolder ? null : helper.getFormatedSize(row.size || 0),
                                    row.modified || null
                                ].filter(Boolean).join(" · ");
                                return (
                                    <li key={row.id} className={classes.row+" "+(isPicked ? classes.rowSelected : "")}>
                                        {
                                            !isFolderMode ?
                                                <input type="checkbox" id={checkId} className={classes.rowCheck} checked={isPicked} onChange={() => toggle(row)}
                                                    aria-label={(isPicked ? "Deselect " : "Select ")+(isFolder ? "folder " : "file ")+row.name} />
                                            : null
                                        }
                                        {
                                            isFolder ?
                                                <Folder className={classes.rowIcon+" "+classes.folderIcon} aria-hidden="true" />
                                            :
                                                <InsertDriveFileOutlined className={classes.rowIcon+" "+classes.fileIcon} aria-hidden="true" />
                                        }
                                        <div className={classes.rowText}>
                                            {
                                                isFolder ?
                                                    <button type="button" className={classes.nameButton} onClick={() => openFolder(row.path)} title={"Open "+row.name}>
                                                        <p className={classes.rowName}>{row.name}</p>
                                                        { meta ? <p className={classes.rowMeta}>{meta}</p> : null }
                                                    </button>
                                                :
                                                    <label htmlFor={checkId} style={{cursor: "pointer", display: "block"}}>
                                                        <p className={classes.rowName} title={row.name}>{row.name}</p>
                                                        { meta ? <p className={classes.rowMeta}>{meta}</p> : null }
                                                    </label>
                                            }
                                        </div>
                                        { isFolder ? <button type="button" className={classes.openButton} onClick={() => openFolder(row.path)} aria-label={"Open folder "+row.name}>Open<ChevronRight aria-hidden="true" /></button> : null }
                                    </li>
                                );
                            })
                    }
                </ul>
                { truncated ? <p className={classes.help}>Only the first 1,000 items are shown. Open a subfolder or search to narrow the list.</p> : null }
                { isFolderMode ? <p className={classes.current}>Selected folder: <strong>{path}</strong> (includes its subfolders)</p> : null }
            </div>
            <div className={classes.footer}>
                {
                    !isFolderMode ?
                        <span className={classes.footerInfo} role="status" aria-live="polite">
                            { pickedRows.length > 0 ? pickedRows.length+" selected"+(pickedSize > 0 ? " · "+helper.getFormatedSize(pickedSize) : "") : "Nothing selected" }
                        </span>
                    : null
                }
                <button type="button" className={classes.cancel} onClick={onClose}>Cancel</button>
                <button type="button" className={classes.accent} onClick={confirm} disabled={!isFolderMode && pickedRows.length <= 0}>
                    { isFolderMode ? "Use this folder" : "Add selected" }
                </button>
            </div>
        </Dialog>
    );
}

MyDocsPickerModal.defaultProps = {
    mode: "items",
    title: "Select from My Docs",
    subtitle: "",
    initialPath: MY_DOCS_ROOT,
    selected: []
};
MyDocsPickerModal.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(["items", "folder"]),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    initialPath: PropTypes.string,
    selected: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired
};

export default MyDocsPickerModal;
